import React, { Component } from 'react';
import { node, func, bool, number, objectOf, any } from 'prop-types';
import isEqual from 'lodash/isEqual';
import compose from 'lodash/flowRight';
import { InstantSearch } from 'react-instantsearch-dom';
import { searchState as searchStateUtil, hasLocationChanged } from 'utils';
import {
    withListingData,
    withAlgoliaAppInfo,
    withFiltersConfig,
    withFeatureFlags,
    withHeader,
    withBrowserHistory,
    withLandingProduct,
} from 'connectors';
import { ListingType, PaginationOffset } from 'contexts';
import {
    locationShape,
    listingDataShape,
    algoliaAppInfoShape,
    filtersConfigShape,
    searchClientShape,
    searchStateShape,
    landingProductShape,
} from 'shapes';

let configure;
let manomano;

class InstantSearchConfigs extends Component {
    static propTypes = {
        children: node.isRequired,
        location: locationShape.isRequired,
        pushHistory: func.isRequired,
        setSearchTitle: func.isRequired,
        mobileWidth: number.isRequired,
        listingData: listingDataShape.isRequired,
        hasRerankingSearchAlgorithm: bool.isRequired,
        hasFFRerankingSearch: bool.isRequired,
        hasInCategorySearch: bool.isRequired,
        hasShopbotRerankingAlternate: bool.isRequired,
        algoliaAppInfo: algoliaAppInfoShape.isRequired,
        resultsState: objectOf(any),
        searchClient: searchClientShape.isRequired,
        initialSearchState: searchStateShape, // eslint-disable-line react/no-unused-prop-types
        filtersConfig: filtersConfigShape.isRequired,
        landingProduct: landingProductShape.isRequired,
        onSearchParameters: func,
    };

    static defaultProps = {
        resultsState: undefined,
        initialSearchState: {},
        onSearchParameters: undefined,
    };

    static getDerivedStateFromProps(props, state) {
        let newState = null;
        if (
            props.location?.pathname !== state.location?.pathname ||
            props.location?.search !== state.location?.search
        ) {
            const nextSearchState = searchStateUtil.urlToAppState(
                props.location,
                props.listingData,
                props.algoliaAppInfo.indexes,
                props.filtersConfig,
                {
                    hasInCategorySearchEnabled: props.hasInCategorySearch,
                },
            );

            const page =
                (nextSearchState.configure && nextSearchState.configure.page) ||
                1;
            nextSearchState.configure = {
                ...configure,
                ...nextSearchState.configure,
            };
            nextSearchState.manomano = {
                ...manomano,
                ...nextSearchState.manomano,
            };

            if (nextSearchState.fromBrowserBack) {
                nextSearchState.configure.page = page;
            }

            newState = {
                ...newState,
                searchState: nextSearchState,
                location: props.location,
            };
        } else if (
            state.searchStateToRestore &&
            !isEqual(state.searchState, state.searchStateToRestore)
        ) {
            // If there is a state to restore and it's different than the current state, we reset it.
            newState = {
                ...newState,
                searchState: state.searchStateToRestore,
                searchStateToRestore: null,
            };
        }

        if (
            state.searchState.manomano &&
            state.searchState.manomano.targetOffset ===
                state.searchState.configure?.offset
        ) {
            if (!newState) {
                newState = { ...state };
            }
            if (newState.searchState.manomano) {
                delete newState.searchState.manomano.targetOffset;
            }
        }

        return newState;
    }

    constructor(props) {
        super(props);
        const searchState =
            props.initialSearchState ||
            searchStateUtil.urlToAppState(
                props.location,
                props.listingData,
                props.algoliaAppInfo.indexes,
                props.filtersConfig,
                {
                    hasInCategorySearchEnabled: props.hasInCategorySearch,
                },
            );

        this.state = {
            searchState,
            indexName: this.props.algoliaAppInfo.defaultIndex,
            location: {}, // eslint-disable-line react/no-unused-state
            searchStateToRestore: null,
            category: null,
        };

        this.mobileMatchMedia = null;

        configure = searchState && searchState.configure;
    }

    componentDidMount() {
        this.mobileMatchMedia = window.matchMedia(
            `(max-width: ${this.props.mobileWidth - 1}px)`,
        );
        this.mobileMatchMedia.addListener(this.handleMobileMatchMediaChange);
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { mobileWidth } = this.props;
        return (
            hasLocationChanged(nextProps.location, this.props.location) ||
            mobileWidth !== nextProps.mobileWidth ||
            this.state.category !== nextState.category
        );
    }

    componentDidUpdate(prevProps, prevState) {
        const { searchStateToRestore, searchState } = this.state;
        if (prevState.searchStateToRestore && !searchStateToRestore) {
            clearTimeout(this.debouncedSetState);
        }
        if (prevState.searchState.query !== searchState.query) {
            this.props.setSearchTitle(searchState.query);
        }
    }

    componentWillUnmount() {
        if (this.mobileMatchMedia) {
            this.mobileMatchMedia.removeListener(
                this.handleMobileMatchMediaChange,
            );
        }
    }

    handleMobileMatchMediaChange = ({ matches: isMobileMode }) => {
        if (
            this.state.isMobileMode !== isMobileMode &&
            this.state.searchState.configure
        ) {
            // only try to restore when we filtered anything
            this.setState({
                searchStateToRestore: this.state.searchState,
            });
        }
    };

    // eslint-disable-next-line react/sort-comp
    handleSearchStateChange = searchState => {
        const { searchState: oldSearchState } = this.state;
        const {
            location,
            listingData,
            pushHistory,
            hasInCategorySearch,
            algoliaAppInfo,
            filtersConfig,
            landingProduct,
        } = this.props;
        const newSearchState = searchState;

        if (!newSearchState.configure) {
            newSearchState.configure = {};
        }

        if (!newSearchState.manomano) {
            newSearchState.manomano = {};
        }

        if (
            searchStateUtil.didUserUpdateSearchState(
                oldSearchState,
                searchState,
                { ignoreNavigation: true },
            )
        ) {
            newSearchState.manomano.targetOffset = 0;
            newSearchState.configure.page = 1;
            newSearchState.manomano.urlParams = {
                ...(hasInCategorySearch &&
                searchState.manomano &&
                searchState.manomano.urlParams &&
                searchState.manomano.urlParams.q
                    ? { q: searchState.manomano.urlParams.q }
                    : {}),
            };
        } else if (
            searchState.configure &&
            newSearchState.manomano.urlParams &&
            searchState.configure.page > 1
        ) {
            newSearchState.manomano.urlParams = {
                ...(hasInCategorySearch &&
                searchState.manomano &&
                searchState.manomano.urlParams &&
                searchState.manomano.urlParams.q
                    ? { q: searchState.manomano.urlParams.q }
                    : {}),
            };
        }

        const customState = {};
        if (this.hasCategoryFilterSelected(searchState)) {
            customState.parentCategory = listingData?.category?.id;
        }

        if (
            landingProduct.modelId &&
            searchStateUtil.hasNoActiveFilter(searchState) &&
            searchState.configure &&
            searchState.configure.page === 1
        ) {
            customState.model_id = landingProduct.modelId;
        }

        if (
            hasInCategorySearch &&
            listingData.type === 'category' &&
            listingData.params.id &&
            searchState.manomano.urlParams?.q
        ) {
            searchState.inCategorySearch = {
                url: location.pathname,
            };
        }

        if (!location.fromBrowserBack) {
            pushHistory(
                searchStateUtil.appStateToUrl({
                    searchState: newSearchState,
                    customState,
                    indexes: algoliaAppInfo.indexes,
                    filtersConfig,
                    flags: {
                        hasInCategorySearchEnabled: hasInCategorySearch,
                    },
                }),
            );
        }

        configure = newSearchState.configure;
        manomano = newSearchState.manomano;
        this.setState({
            searchState: newSearchState,
        });
    };

    hasCategoryFilterSelected(searchState = this.state.searchState) {
        return (
            searchState.menu &&
            Object.entries(searchState.menu).some(
                ([key, value]) =>
                    key.match(/categories\.l[2-3]\.id/) && !!value,
            )
        );
    }

    render() {
        const {
            children,
            resultsState,
            searchClient,
            onSearchParameters,
            listingData,
            landingProduct,
            hasFFRerankingSearch,
            hasInCategorySearch,
            hasRerankingSearchAlgorithm,
            hasShopbotRerankingAlternate,
        } = this.props;
        const { searchState, indexName } = this.state;

        const rulesContext = searchStateUtil.getRulesContext(
            searchState,
            listingData,
            landingProduct.model_id,
            {
                hasFFRerankingSearch,
                hasInCategorySearch,
                hasRerankingSearchAlgorithm,
                hasShopbotRerankingAlternate,
            },
        );

        return (
            <InstantSearch
                indexName={indexName}
                searchClient={searchClient}
                searchState={searchState}
                resultsState={resultsState}
                onSearchStateChange={this.handleSearchStateChange}
                onSearchParameters={onSearchParameters}
            >
                <ListingType>
                    <PaginationOffset
                        page={
                            (searchState.configure &&
                                parseInt(searchState.configure.page, 10)) ||
                            1
                        }
                        searchState={searchState}
                        ruleContexts={rulesContext}
                    >
                        {children}
                    </PaginationOffset>
                </ListingType>
            </InstantSearch>
        );
    }
}

export default compose(
    withFeatureFlags({
        mobileWidth: 'instantSearch.mobileWidth',
        hasRerankingSearchAlgorithm: 'relevance.reranking.search.algorithm',
        hasFFRerankingSearch: 'ff.relevance.reranking.search',
        hasInCategorySearch: 'relevance.in.category.search',
        hasShopbotRerankingAlternate: 'relevance.shopbot.reranking.alternate',
    }),
    withBrowserHistory,
    withHeader,
    withListingData,
    withAlgoliaAppInfo,
    withFiltersConfig,
    withLandingProduct,
)(InstantSearchConfigs);
